import { useEffect, useRef, useState } from 'react';

function indexOfNearestChild(track) {
  const kids = track.children;
  if (!kids.length) return 0;
  const target = track.scrollLeft + track.clientWidth / 2;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < kids.length; i += 1) {
    const child = kids[i];
    const point = child.offsetLeft + child.offsetWidth / 2;
    const dist = Math.abs(point - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

export default function LogoCarousel({ companies }) {
  const items = companies || [];
  const trackRef = useRef(null);
  const jumpTimerRef = useRef(null);
  const programmaticRef = useRef(false);
  const [active, setActive] = useState(0);
  const loop = items.length > 1;
  const lastIndex = Math.max(0, items.length - 1);

  const markProgrammatic = (ms = 520) => {
    programmaticRef.current = true;
    window.clearTimeout(jumpTimerRef.current);
    jumpTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false;
    }, ms);
  };

  const scrollToChild = (childIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[childIndex];
    if (!card) return;
    const left = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left, behavior });
    } else {
      track.scrollLeft = left;
    }
  };

  const afterScroll = (fn) => {
    const track = trackRef.current;
    if (!track) {
      fn();
      return;
    }
    window.clearTimeout(jumpTimerRef.current);
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      track.removeEventListener('scrollend', done);
      window.clearTimeout(jumpTimerRef.current);
      programmaticRef.current = false;
      fn();
    };
    track.addEventListener('scrollend', done);
    jumpTimerRef.current = window.setTimeout(done, 480);
  };

  const scrollToStart = (startIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track || !items.length) return;
    markProgrammatic();

    if (loop) {
      if (startIndex > lastIndex) {
        setActive(0);
        scrollToChild(items.length + 1, behavior);
        afterScroll(() => scrollToChild(1, 'auto'));
        return;
      }
      if (startIndex < 0) {
        setActive(lastIndex);
        scrollToChild(0, behavior);
        afterScroll(() => scrollToChild(items.length, 'auto'));
        return;
      }
      const next = Math.min(Math.max(0, startIndex), lastIndex);
      scrollToChild(next + 1, behavior);
      setActive(next);
      return;
    }

    const next = Math.min(Math.max(0, startIndex), lastIndex);
    scrollToChild(next, behavior);
    setActive(next);
  };

  useEffect(() => {
    scrollToStart(0, 'auto');
    return () => window.clearTimeout(jumpTimerRef.current);
  }, [items.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !items.length) return undefined;
    let settleTimer;

    const syncFromScroll = (finalize) => {
      if (programmaticRef.current) return;
      const displayIndex = indexOfNearestChild(track);
      if (loop) {
        if (displayIndex <= 0) {
          setActive(lastIndex);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(items.length, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        if (displayIndex >= items.length + 1) {
          setActive(0);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(1, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        setActive(displayIndex - 1);
        return;
      }
      setActive(displayIndex);
    };

    const onScroll = () => {
      syncFromScroll(false);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => syncFromScroll(true), 90);
    };
    const onScrollEnd = () => {
      window.clearTimeout(settleTimer);
      syncFromScroll(true);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('scrollend', onScrollEnd);
    return () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', onScrollEnd);
      window.clearTimeout(settleTimer);
    };
  }, [items.length, loop, lastIndex]);

  if (!items.length) return null;

  const slots = loop
    ? [
        { item: items[items.length - 1], originalIndex: items.length - 1 },
        ...items.map((item, originalIndex) => ({ item, originalIndex })),
        { item: items[0], originalIndex: 0 },
      ]
    : items.map((item, originalIndex) => ({ item, originalIndex }));

  return (
    <div className="partner-carousel-shell">
      <div className="ceo-carousel">
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--prev"
          onClick={() => scrollToStart(active - 1)}
          aria-label="חברה קודמת"
        >
          ‹
        </button>
        <div className="ceo-carousel-track" ref={trackRef}>
          {slots.map((slot, displayIndex) => (
            <article
              key={`${slot.item.companyKey}-${displayIndex}`}
              className={`partner-company-item${slot.item.variant === 'dark' ? ' partner-company-item--dark' : ''}${slot.originalIndex === active ? ' is-active' : ''}`}
              onClick={() => scrollToStart(slot.originalIndex)}
            >
              <img
                src={slot.item.link}
                alt={slot.item.alt || slot.item.name}
                className="partner-company-logo"
              />
            </article>
          ))}
        </div>
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--next"
          onClick={() => scrollToStart(active + 1)}
          aria-label="חברה הבאה"
        >
          ›
        </button>
      </div>
    </div>
  );
}
